// frontend/src/modules/payments/pages/PaymentsPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { FileDown } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { GlassCard, BentoGrid, Eyebrow } from '../../../design-system/pds';
import { WaveformBars, ConcentricRingChart, Sparkline } from '../../../design-system/charts';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { usePermission } from '../../../core/auth/usePermission';
import { formatMoney } from '../../../design-system/format';
import { downloadFile } from '../../payroll/api';
import { SetupTab } from './SetupTab';
import { HistoryTab } from './HistoryTab';

interface CurrencySummary {
  currency: string;
  total_collected: string;
  last_30_days: string;
  count: number;
}

export function PaymentsPage() {
  const [tab, setTab] = React.useState<'setup' | 'history'>('history');
  const { tenant } = useBranding();
  const fallbackCurrency = (tenant as any)?.currency ?? 'USD';
  const canExport = usePermission('payments.manage');
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadFile('/api/payments/export', `payments-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err: any) {
      toast.error('Export failed', { description: err.message ?? String(err) });
    } finally {
      setExporting(false);
    }
  };

  // The paged list backs only the 60-day timeline visual (capped at 200 rows).
  const { data } = useApi<{ rows: Array<{ amount: string | number; currency: string; paid_at: string }> }>('/api/payments?limit=200');
  const rows = data?.rows ?? [];

  // KPI totals come from a SQL aggregate over ALL payments — summing the capped
  // page undercounts any tenant with >200 payments. The summary is per-currency
  // and NET of succeeded refunds; `primary` is the tenant-currency row (or the
  // largest currency when the tenant currency has no payments).
  const { data: summary } = useApi<{ currencies: CurrencySummary[]; primary: CurrencySummary }>('/api/payments/summary');
  const currency = summary?.primary?.currency ?? fallbackCurrency;
  const total = Number(summary?.primary?.total_collected ?? 0);
  const last30 = Number(summary?.primary?.last_30_days ?? 0);
  const currencies = summary?.currencies ?? [];
  const txnCount = currencies.length > 0
    ? currencies.reduce((s, c) => s + (c.count || 0), 0)
    : rows.length;
  const otherCurrencies = currencies.filter((c) => c.currency !== currency);

  // Real gateway state — the "connected" badge must reflect it, not be hardcoded.
  const { data: gw } = useApi<{ config: { api_secret_set: boolean; publishable_key: string } | null }>('/api/payments/gateway-config');
  const connected = !!(gw?.config?.api_secret_set && gw?.config?.publishable_key);

  // Daily waveform for last 60 days
  const waveform = React.useMemo(() => {
    const days = 60;
    const buckets = new Array(days).fill(0);
    const now = Date.now();
    for (const p of rows) {
      const t = new Date(p.paid_at).getTime();
      if (!Number.isFinite(t)) continue;
      const idx = days - 1 - Math.floor((now - t) / (24 * 60 * 60 * 1000));
      if (idx >= 0 && idx < days) buckets[idx] += Number(p.amount) || 0;
    }
    const max = Math.max(1, ...buckets);
    return buckets.map((v) => Math.max(8, (v / max) * 100));
  }, [rows]);

  const sparkline = waveform.map((v) => v);

  const tiers: Array<{ label: string; size: number; fill: string; solid?: boolean }> = [
    { label: formatMoney(total, currency), size: 240, fill: '#FFE3D8' },
    { label: formatMoney(total * 0.7, currency), size: 180, fill: '#FFC7B2' },
    { label: formatMoney(last30, currency), size: 120, fill: '#FF8F66' },
    { label: '30D', size: 64, fill: '#FF7A5C', solid: true },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Money · Payments"
        title="Payments"
        description="Connect Stripe, share pay links on invoices, and audit every payment you've received."
        actions={canExport ? (
          <Button size="default" variant="outline" className="gap-1.5" disabled={exporting} onClick={handleExport}>
            <FileDown className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export to accounting'}
          </Button>
        ) : undefined}
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-6">
        <BentoGrid cols={12}>
          {/* Big total + waveform */}
          <GlassCard className="col-span-12 lg:col-span-8" padding="p-7">
            <div className="flex items-end justify-between mb-5 gap-3">
              <div className="min-w-0">
                <Eyebrow className="mb-2">Total collected{otherCurrencies.length > 0 ? ` · ${currency}` : ''}</Eyebrow>
                <p className="text-stat-lg tabular-nums font-bold text-ink dark:text-white leading-none truncate">{formatMoney(total, currency)}</p>
                <p className="text-[13.5px] text-ink/55 dark:text-white/55 mt-2">{formatMoney(last30, currency)} in the last 30 days · {txnCount} transactions · net of refunds</p>
                {otherCurrencies.length > 0 && (
                  <p className="text-[12.5px] text-ink/45 dark:text-white/45 mt-1.5 tabular-nums truncate">
                    Also collected:{' '}
                    {otherCurrencies
                      .map((c) => `${formatMoney(Number(c.total_collected), c.currency)} (${c.count} txn${c.count === 1 ? '' : 's'})`)
                      .join(' · ')}
                  </p>
                )}
              </div>
              {connected ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]">
                  Stripe connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink/5 text-ink/50 dark:text-white/50 dark:bg-white/10 dark:text-white/50 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]">
                  Stripe not connected
                </span>
              )}
            </div>
            {rows.length > 0 ? (
              <WaveformBars data={waveform} height={110} />
            ) : (
              <div className="h-[110px] rounded-2xl bg-pearl-100 dark:bg-white/[0.05] flex items-center justify-center text-[13px] text-ink/45 dark:text-white/45">
                No payments yet — your activity timeline will appear here.
              </div>
            )}
          </GlassCard>

          {/* Concentric rings */}
          <GlassCard className="col-span-12 lg:col-span-4" padding="p-7">
            <Eyebrow className="mb-3">Cohort spread</Eyebrow>
            <div className="flex items-center justify-center pt-2">
              <ConcentricRingChart rings={tiers} className="!w-60 !h-60" />
            </div>
          </GlassCard>
        </BentoGrid>
      </div>

      <div className="px-6 sm:px-8 lg:px-10 pb-12">
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList className="bg-white/55 dark:bg-white/[0.05] backdrop-blur-md ring-1 ring-white/40 dark:ring-white/10 rounded-full p-1 h-11 inline-flex shadow-pill dark:shadow-none">
            <TabsTrigger value="history" className="rounded-full px-5 h-9 text-[12.5px] font-semibold data-[state=active]:bg-ink-900 data-[state=active]:text-white">History</TabsTrigger>
            <TabsTrigger value="setup"   className="rounded-full px-5 h-9 text-[12.5px] font-semibold data-[state=active]:bg-ink-900 data-[state=active]:text-white">Setup</TabsTrigger>
          </TabsList>
          <TabsContent value="history" className="pt-6"><HistoryTab /></TabsContent>
          <TabsContent value="setup" className="pt-6"><SetupTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
